import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import DataTable from '../components/common/DataTable';
import './Spots.css';

const Spots = ({ serverId, radioTitle }) => {
  const [spots, setSpots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [audioPlaying, setAudioPlaying] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    fetch(`https://radio.amgroupmkt.com:1030/api/v2/jingle_schemes/?server=${serverId}`)
      .then(response => response.json())
      .then(data => {
        const activeSpots = data.filter(spot => spot.is_currently_active);
        setSpots(activeSpots);
      })
      .catch(error => console.error(error));
  }, [serverId]);

  const handleCategoryClick = (categoryName) => {
    fetch(`https://radio.amgroupmkt.com:1030/api/v2/jingle_categories/?server=${serverId}`)
      .then(response => response.json())
      .then(data => {
        const category = data.find(category => category.name === categoryName);
        if (category) {
          const spotsData = category.jingles.map(jingle => ({
            ...jingle,
            duration: jingle.duration || 0,
            size: jingle.size || 0,
            created: jingle.created || new Date().toISOString(),
            category: categoryName
          }));
          setModalContent(spotsData);
          setShowModal(true);
        }
      })
      .catch(error => console.error(error));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handlePlayAudio = (jingleId) => {
    if (playingId === jingleId && audioPlaying) {
      audioPlaying.pause();
      audioPlaying.currentTime = 0;
      setAudioPlaying(null);
      setPlayingId(null);
      return;
    }
    
    if (audioPlaying) {
      audioPlaying.pause();
      audioPlaying.currentTime = 0;
    }
    
    const audio = new Audio(`https://radio.amgroupmkt.com:1030/api/v2/jingles/${jingleId}/download/`);
    audio.play();
    setAudioPlaying(audio);
    setPlayingId(jingleId);
  };

  const downloadTable = async () => {
    try {
      const response = await fetch(`https://radio.amgroupmkt.com:1030/api/v2/jingle_categories/?server=${serverId}`);
      const data = await response.json();

      const workbook = XLSX.utils.book_new();
      
      data.forEach(category => {
        const jinglesData = category.jingles.map(jingle => ({
          'ID': jingle.id,
          'Nombre': jingle.filename,
          'Categoría': category.name,
          'Duración': formatDuration(jingle.length),
          
        }));

        if (jinglesData.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(jinglesData);
          XLSX.utils.book_append_sheet(workbook, worksheet, category.name.substring(0, 31));
        }
      });

      XLSX.writeFile(workbook, `spots_${radioTitle || 'radio'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel exportado correctamente');

    } catch (error) {
      console.error('Error al descargar:', error);
      toast.error('Error al exportar el Excel');
    }
  };

  const formatDuration = (length) => {
    // Convertir a segundos (length está en milisegundos)
    const totalSeconds = Math.floor(length / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`https://radio.amgroupmkt.com:1030/api/v2/jingle_categories/?server=${serverId}`);
      const data = await response.json();

      const dataToDownload = data.map(category => category.jingles.map(jingle => {
        const group = spots.find(spot => spot.group === jingle.group);
        return {
          'Radio': radioTitle || 'Sin nombre',
          'Categoría': category.name,
          'Nombre de archivo': jingle.filename,
          'Duración': formatDuration(jingle.length),
          'Fecha de inicio': group ? new Date(group.rotation_start_ts).toLocaleDateString() : 'N/A',
          'Fecha de salida': group ? new Date(group.rotation_end_ts).toLocaleDateString() : 'N/A',
          'Tipo de programación': group && group.time_interval_minute ? `${group.time_interval_minute} minutos` : 'Entre pistas',
          'Estado': group && group.is_currently_active ? 'Activo' : 'Inactivo'
        };
      })).flat();

      const ws = XLSX.utils.json_to_sheet(dataToDownload);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `Spots - ${radioTitle || 'Radio'}`);
      XLSX.writeFile(wb, `spots_${radioTitle || 'radio'}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success('Lista descargada exitosamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al descargar la lista');
    }
  };

  const columns = [
    { key: 'rotation_start_ts', label: 'Fecha de inicio', sortable: true, 
      render: (item) => new Date(item.rotation_start_ts).toLocaleDateString() },
    { key: 'rotation_end_ts', label: 'Fecha de salida', sortable: true,
      render: (item) => new Date(item.rotation_end_ts).toLocaleDateString() },
    { key: 'category_name', label: 'Categoría', sortable: true },
    { key: 'time_interval_minute', label: 'Intervalo', 
      render: (item) => item.time_interval_minute ? `${item.time_interval_minute} min` : '-' },
    { key: 'break_track', label: 'Entre canciones',
      render: (item) => !item.time_interval_minute ? item.break_track : '-' },
    { key: 'is_currently_active', label: 'Estado', sortable: true,
      render: (item) => (
        <span className={`status-badge ${item.is_currently_active ? 'active' : 'inactive'}`}>
          {item.is_currently_active ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];

  const actions = [
    {
      icon: 'fas fa-list',
      variant: 'link',
      title: 'Ver spots',
      onClick: (item) => handleCategoryClick(item.category_name)
    }
  ];

  const spotColumns = [
    { 
      key: 'filename', 
      label: 'Nombre del Archivo', 
      sortable: true 
    },
    {
      key: 'category',
      label: 'Categoría',
      sortable: true
    },
    {
      key: 'duration',
      label: 'Duración',
      sortable: true,
      render: (item) => formatDuration(item.length)
    },
    {
      key: 'actions',
      label: '',
      className: 'action-column',
      render: (item) => (
        <div className="spot-actions">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{playingId === item.id ? 'Detener' : 'Reproducir'}</Tooltip>}
          >
            <Button 
              variant="link" 
              size="sm"
              className="action-btn play"
              onClick={() => handlePlayAudio(item.id)}
            >
              <i className={`fas ${playingId === item.id ? 'fa-stop' : 'fa-play'}`}></i>
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Descargar</Tooltip>}
          >
            <Button 
              variant="link" 
              size="sm"
              className="action-btn download"
              href={`https://radio.amgroupmkt.com:1030/api/v2/jingles/${item.id}/download/`}
              download
            >
              <i className="fas fa-download"></i>
            </Button>
          </OverlayTrigger>
        </div>
      )
    }
  ];

  return (
    <>
      <DataTable
        title="Spots Programados"
        data={spots}
        columns={columns}
        actions={actions}
        exportable={true}
        onExport={handleDownload}
        sortConfig={sortConfig}
        onSort={handleSort}
      />

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        className="spots-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Lista de Spots</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DataTable
            data={modalContent}
            columns={spotColumns}
            searchable={true}
            pagination={true}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default Spots;